// src/utils/manuscriptTheme.js
//
// A manuscript theme — the look of one book — as the browser draws it: in the
// editor, and in the Theme Editor's preview. **Not `src/theme.js`**, which is the
// app's own design tokens; nothing here is product chrome.
//
// The printed book is drawn by `backend/utils/manuscript_theme.py` from the same
// values. Where the two differ it is because the PDF engine cannot do something
// the browser can, and the backend module lists each one. The visible
// difference is small caps: the browser draws the first line of a chapter in
// them, the PDF its first five words.
//
// Nothing here writes into the manuscript. The look is a stylesheet scoped to
// whatever element holds the pages, and the chapter labels are CSS counters, so
// the stored HTML stays exactly what the author typed. The one exception is
// `clearOverrides`, which the author asks for: it takes out the old inline
// fonts and sizes that would otherwise sit on top of any theme.

import { fontStackFor } from '../config/fonts';
import { ART_SAMPLES, BASE_THEME, THEME_SECTIONS, mergeTheme } from '../config/manuscriptThemes';
import { API_BASE } from './api';

export const SCENE_BREAK_CLASS = 'rv-scene-break';

const px = (points) => (points * 4) / 3;
const n = (value) => Number(value.toFixed(2));

/** A theme's values only — the sections, without name, id or blurb. */
export function themeValues(theme) {
  const out = {};
  THEME_SECTIONS.forEach((section) => { out[section] = { ...(theme?.[section] || {}) }; });
  return out;
}

/** Any stored or partial theme, completed from the defaults. */
export function completeTheme(theme) {
  return { ...(theme || {}), ...mergeTheme(BASE_THEME, theme || {}) };
}

/** Two themes look the same: every value in every section equal. */
export function sameLook(a, b) {
  return JSON.stringify(themeValues(completeTheme(a))) === JSON.stringify(themeValues(completeTheme(b)));
}

// --------------------------------------------------------------------------
// Chapter labels

const WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen',
  'Nineteen', 'Twenty', 'Twenty-One', 'Twenty-Two', 'Twenty-Three', 'Twenty-Four', 'Twenty-Five',
];

const roman = (number) => {
  let out = '';
  let rest = number;
  [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'],
    [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']].forEach(([value, numeral]) => {
    while (rest >= value) { out += numeral; rest -= value; }
  });
  return out;
};

/** The generated label above a chapter title — "Chapter One", "IV", "07". */
export function chapterLabel(style, number) {
  const word = WORDS[number] || String(number);
  switch (style) {
    case 'word': return `Chapter ${word}`;
    case 'num': return `Chapter ${number}`;
    case 'numeral': return String(number);
    case 'padded': return String(number).padStart(2, '0');
    case 'roman': return roman(number);
    case 'wordonly': return word;
    default: return '';
  }
}

// The same labels as CSS, counted over the pages so nothing is stored.
export const CHAPTER_COUNTER = 'rv-chapter';
const COUNTER = CHAPTER_COUNTER;
const WORD_STYLE = 'rv-chapter-words';
const counterStyleCss = `@counter-style ${WORD_STYLE}{system:fixed;symbols:${WORDS.slice(1).map((w) => `"${w}"`).join(' ')};fallback:decimal}`;

function labelContent(style) {
  switch (style) {
    case 'word': return `"Chapter " counter(${COUNTER}, ${WORD_STYLE})`;
    case 'num': return `"Chapter " counter(${COUNTER})`;
    case 'numeral': return `counter(${COUNTER})`;
    case 'padded': return `counter(${COUNTER}, decimal-leading-zero)`;
    case 'roman': return `counter(${COUNTER}, upper-roman)`;
    case 'wordonly': return `counter(${COUNTER}, ${WORD_STYLE})`;
    default: return null;
  }
}

// --------------------------------------------------------------------------
// The stylesheet

// Small caps the way the PDF draws them — capitals at 80% — so a label or a
// heading looks the same on screen as on paper.
function caseCss(textCase, sizePx) {
  if (textCase === 'upper') return `text-transform:uppercase;font-variant-caps:normal;font-size:${n(sizePx)}px;`;
  if (textCase === 'smallcaps') return `text-transform:uppercase;font-variant-caps:normal;font-size:${n(sizePx * 0.8)}px;`;
  return `text-transform:none;font-variant-caps:normal;font-size:${n(sizePx)}px;`;
}

const svgUrl = (svg) => `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;

// --------------------------------------------------------------------------
// Page art

/** The printer's bleed, in inches: past the trim on the outside, top and bottom. */
export const BLEED_IN = 0.125;

/** Where the browser loads a theme's art from, or '' for none. */
export function artUrl(art) {
  const src = art?.src || '';
  if (src.startsWith('sample:')) {
    const id = src.slice(7);
    return ART_SAMPLES[id] ? `${process.env.PUBLIC_URL || ''}/theme-art/${id}.svg` : '';
  }
  if (src.startsWith('upload:')) return `${API_BASE}/api/manuscript-theme-art/${src.slice(7)}`;
  return '';
}

/** Whether a theme puts art on any page at all. */
export function artOn(theme) {
  const art = theme?.art;
  return Boolean(art?.on && artUrl(art) && Object.values(art.pages || {}).some(Boolean));
}

export function artName(art) {
  const src = art?.src || '';
  if (src.startsWith('sample:')) return ART_SAMPLES[src.slice(7)]?.name || 'Sample';
  return art?.name || 'Uploaded image';
}

/** The size the art is drawn at for Actual and Tile, in CSS pixels. */
function artWidthPx(art) {
  const sample = (art.src || '').startsWith('sample:') ? ART_SAMPLES[art.src.slice(7)] : null;
  return ((sample ? sample.w : art.w) || 600) * (art.scale / 100);
}

const ART_ANCHOR = {
  tl: 'left top', tc: 'center top', tr: 'right top', cl: 'left center', cc: 'center center',
  cr: 'right center', bl: 'left bottom', bc: 'center bottom', br: 'right bottom',
};

const FADES = {
  vignette: 'radial-gradient(ellipse at center,#000 40%,transparent 82%)',
  bottom: 'linear-gradient(to bottom,#000 30%,transparent 92%)',
  top: 'linear-gradient(to top,#000 30%,transparent 92%)',
};
const mask = (m) => (m ? `-webkit-mask-image:${m};mask-image:${m};` : '');

/**
 * The art as a layer under each page's text: the page's `::before`, so nothing
 * is added to the manuscript. Which pages carry it — chapter openers, body
 * pages, blanks — is told apart by `:has()` in the editor and by classes in
 * the preview (`rv-opener`, `rv-blank`), which knows where its blank pages are.
 */
function artCss(t, S, { sides, top, bottom, inside, outside }) {
  const a = t.art;
  const url = artUrl(a);
  const byClass = sides === 'class';
  const verso = byClass ? '.rv-verso' : ':nth-child(even of .rv-page)';
  const opener = byClass ? '.rv-opener' : ':has(> .rv-page-body > [data-heading-level="1"]:first-child)';
  const kinds = {
    opener: `${S} .rv-page${opener}`,
    body: byClass ? `${S} .rv-page:not(.rv-opener):not(.rv-blank)` : `${S} .rv-page:not(${opener})`,
    blank: byClass ? `${S} .rv-page.rv-blank` : null,
  };
  const pages = Object.entries(kinds).filter(([k, sel]) => sel && a.pages[k]).map(([, sel]) => sel);
  if (!pages.length) return [];

  const bleed = BLEED_IN * 96;
  const box = {
    bleed: `top:${-bleed}px;right:${-bleed}px;bottom:${-bleed}px;left:${-bleed}px;`,
    trim: 'top:0;right:0;bottom:0;left:0;',
    margins: `top:${n(top)}px;bottom:${n(bottom)}px;left:${n(inside)}px;right:${n(outside)}px;`,
    inset: `top:${n(a.inset * 96)}px;right:${n(a.inset * 96)}px;bottom:${n(a.inset * 96)}px;left:${n(a.inset * 96)}px;`,
  }[a.extent];
  const px = n(artWidthPx(a));
  const size = { cover: 'cover', contain: 'contain', stretch: '100% 100%', tile: `${px}px auto`, actual: `${px}px auto` }[a.fit];
  const filter = [a.filter === 'grayscale' ? 'grayscale(1)' : a.filter === 'sepia' ? 'sepia(.85)' : '', a.blur > 0 ? `blur(${a.blur}px)` : '']
    .filter(Boolean).join(' ') || 'none';
  const gutterRecto = 'linear-gradient(to right,transparent 0%,#000 55%)';
  const gutterVerso = 'linear-gradient(to left,transparent 0%,#000 55%)';

  const css = [
    `${pages.map((p) => `${p}::before`).join(',')}{content:"";position:absolute;z-index:0;pointer-events:none;${box}`
      + `background-image:url("${url}");background-repeat:${a.fit === 'tile' ? 'repeat' : 'no-repeat'};background-size:${size};`
      + `background-position:${ART_ANCHOR[a.anchor] || 'center'};opacity:${a.opacity / 100};mix-blend-mode:${a.blend};filter:${filter};`
      + `${a.fade === 'gutter' ? mask(gutterRecto) : mask(FADES[a.fade])}}`,
    `${S} .rv-page{isolation:isolate}`,
    `${S} .rv-page-body{position:relative;z-index:1}`,
  ];
  // The text block moves with the spine, so art set to it does too.
  if (a.extent === 'margins') {
    css.push(`${pages.map((p) => `${p}${verso}::before`).join(',')}{left:${n(outside)}px;right:${n(inside)}px}`);
  }
  // A left-hand page: the art mirrored, if asked, and the gutter fade on its right
  // — the spine side. Mirroring flips the mask with the art, so it keeps the
  // recto's gradient then.
  const versoRules = [];
  if (a.mirror) versoRules.push('transform:scaleX(-1);');
  if (a.fade === 'gutter') versoRules.push(mask(a.mirror ? gutterRecto : gutterVerso));
  if (versoRules.length) {
    css.push(`${pages.map((p) => `${p}${verso}::before`).join(',')}{${versoRules.join('')}}`);
  }
  return css;
}

const flourishSvg = (color) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 16"><path d="M2 8c14 0 22-5 34-1 7 2 11 6 17 5 4-1 5-4 7-4s3 3 7 4c6 1 10-3 17-5 12-4 20 1 34 1" fill="none" stroke="${color}" stroke-width="1.2"/><circle cx="60" cy="8" r="2.1" fill="${color}"/></svg>`;

const ORNAMENT_TEXT = {
  fleuron: '\\2766', dots: '\\2022\\2002\\2022\\2002\\2022', diamond: '\\25C6', stars: '\\2726',
};

const BREAK_TEXT = {
  asterism: '*\\2002*\\2002*', dinkus: '\\2042', fleuron: '\\2766', hash: '#',
  dots: '\\2022\\2002\\2022\\2002\\2022', stars: '\\2726\\2002\\2726\\2002\\2726', diamond: '\\25C6',
  rule: '', space: '\\00a0',
};

const LIST_MARKER = { bullet: '"\\2022  "', dash: '"\\2013  "', circle: '"\\25E6  "', arrow: '"\\203A  "' };

/**
 * The theme as a stylesheet over a set of `.rv-page` sheets.
 *
 * `scope` is the selector of the element that holds the pages: the editor's
 * scroll area, or the Theme Editor's preview. `page` describes the page chrome
 * around the text block, so the margins land where the theme puts them:
 *   - `{ headerPx, footerPx }` for the editor, whose pages carry a header and a
 *     footer band inside the top and bottom margins;
 *   - omitted for a page that is only its text block.
 *
 * `sides` says how a page knows it is a left-hand one: by its place among the
 * pages (`'order'`, the editor, where every page is on screen), or by the
 * `rv-verso` class (`'class'`, the preview, which shows two pages at a time).
 * The margins are `!important` because a page body can carry inline padding
 * from the ruler, saved with the manuscript before themes existed.
 */
export function themeCss(themeIn, {
  scope, page = { headerPx: 0, footerPx: 0 }, trimHeightIn = 9, sides = 'order',
} = {}) {
  const t = completeTheme(themeIn);
  const S = scope;
  const B = `${S} .rv-page-body`;
  const { body: b, ch: c, h, q, col } = t;
  const bpx = px(b.size);
  const lead = bpx * b.lead;
  const indent = b.style === 'indent' ? b.indent : 0;
  const gap = b.style === 'spaced' ? lead * 0.6 : 0;
  const lpx = px(c.lSize);
  const tpx = px(c.tSize);
  const hpx = px(h.size);
  const hy = b.hyph ? 'auto' : 'manual';
  const label = labelContent(c.label);
  const titleTop = c.label === 'none' ? 0 : (lpx > 32 ? 0.12 : 0.45) * tpx;
  const textBlockPx = (trimHeightIn - t.page.top - t.page.bottom) * 96;
  const accent = col.accent;
  const headerPx = page.headerPx || 0;
  const footerPx = page.footerPx || 0;
  const top = Math.max(0, t.page.top * 96 - headerPx);
  const bottom = Math.max(0, t.page.bottom * 96 - footerPx);
  const inside = t.page.inside * 96;
  const outside = t.page.outside * 96;

  const css = [
    counterStyleCss,
    `${S}{counter-reset:${COUNTER}}`,
    `${S} .rv-page{background:${col.paper}}`,
    // Margins: the inside one is on the spine side, so it swaps sides on every
    // other page. The first page of the book is a right-hand page.
    `${B}{padding:${n(top)}px ${n(outside)}px ${n(bottom)}px ${n(inside)}px !important;color:${col.text};font-family:${fontStackFor(b.font)};font-size:${n(bpx)}px;line-height:${n(lead)}px}`,
    `${S} .rv-page${sides === 'class' ? '.rv-verso' : ':nth-child(even of .rv-page)'} .rv-page-body{padding-left:${n(outside)}px !important;padding-right:${n(inside)}px !important}`,
    `${B} p{margin:0 0 ${n(gap)}px;line-height:${n(lead)}px;text-indent:${n(indent)}em;text-align:${b.align};hyphens:${hy} !important;-webkit-hyphens:${hy} !important}`,
    `${B} > [data-heading-level] + p,${B} > .${SCENE_BREAK_CLASS} + p,${B} p[data-split-cont]{text-indent:0}`,
    `${B} p[data-split-id]{margin-bottom:0}`,
    // A chapter's title, dropped down its page, with its label above it and its
    // ornament below — both drawn by the browser, so neither is stored.
    `${B} [data-heading-level="1"]{counter-increment:${COUNTER};font-family:${fontStackFor(c.tFont)};line-height:1.18;font-weight:${c.tWeight};font-style:${c.tItalic ? 'italic' : 'normal'};${caseCss(c.tCase, tpx)}letter-spacing:${c.tCase === 'upper' ? '.06em' : c.tCase === 'smallcaps' ? '.03em' : '0'};text-align:${c.align};color:${col.text};text-indent:0;margin:0 0 ${n(lead * 2)}px;hyphens:none !important}`,
    `${B} > [data-heading-level="1"]:first-child{padding-top:${n(textBlockPx * c.sink / 100)}px}`,
  ];

  if (label) {
    css.push(`${B} [data-heading-level="1"]::before{content:${label};display:block;font-family:${fontStackFor(c.lFont)};line-height:1.1;font-weight:400;font-style:normal;letter-spacing:${c.lTrack}em;color:${accent};${caseCss(c.lCase, lpx)}margin-bottom:${n(titleTop)}px}`);
  }

  const ornBase = `content:"";display:block;margin-top:${n(lead * 0.8)}px;color:${accent};font-size:${n(bpx * 1.1)}px;line-height:1;font-family:${fontStackFor(b.font)};font-style:normal;font-weight:400;text-transform:none;letter-spacing:0;`;
  const ornSide = c.align === 'center' ? 'margin-left:auto;margin-right:auto;' : '';
  if (c.orn === 'rule') {
    css.push(`${B} [data-heading-level="1"]::after{${ornBase}width:3.2em;height:0;border-top:1px solid ${accent};${ornSide}}`);
  } else if (c.orn === 'flourish') {
    css.push(`${B} [data-heading-level="1"]::after{${ornBase}width:7.5em;height:1em;background:${svgUrl(flourishSvg(accent))} no-repeat center/contain;${ornSide}}`);
  } else if (ORNAMENT_TEXT[c.orn]) {
    css.push(`${B} [data-heading-level="1"]::after{${ornBase}content:"${ORNAMENT_TEXT[c.orn]}";text-align:${c.align}}`);
  }

  css.push(
    `${B} [data-heading-level="2"],${B} [data-heading-level="3"],${B} [data-heading-level="4"]{font-family:${fontStackFor(h.font)};font-weight:${h.weight};font-style:${h.italic ? 'italic' : 'normal'};line-height:${n(lead)}px;${caseCss(h.case, hpx)}letter-spacing:${h.case === 'upper' ? '.1em' : h.case === 'smallcaps' ? '.05em' : '0'};text-align:${h.align};color:${h.color === 'accent' ? accent : col.text};margin:${n(lead)}px 0 ${n(lead * 0.5)}px;text-indent:0;hyphens:none !important}`,
    `${B} > [data-heading-level]:first-child{margin-top:0}`,
    `${B} .${SCENE_BREAK_CLASS}{text-align:center;line-height:${n(lead)}px;height:${n(lead)}px;margin:${n(lead * 0.5)}px 0;color:${accent};text-indent:0;user-select:none}`,
    `${B} .${SCENE_BREAK_CLASS}::before{content:"${BREAK_TEXT[t.brk.style] ?? ''}";${t.brk.style === 'rule' ? `display:inline-block;width:4em;border-top:1px solid ${accent};vertical-align:middle;` : ''}${['asterism', 'dots', 'stars'].includes(t.brk.style) ? 'letter-spacing:.1em;' : ''}${['dinkus', 'fleuron', 'diamond'].includes(t.brk.style) ? 'font-size:1.15em;' : ''}}`,
    `${B} blockquote{margin:${n(lead * 0.5)}px ${q.indent}em;font-size:${q.size}%;line-height:${n(lead * Math.max(0.92, q.size / 100))}px;font-style:${q.italic ? 'italic' : 'normal'};text-align:${b.align};text-indent:0;${q.rule ? `border-left:2px solid ${accent};padding-left:.85em;` : ''}}`,
    `${B} ul{margin:${n(lead * 0.5)}px 0;padding-left:${n(Math.max(indent, 1.2) + 0.4)}em;list-style-type:${LIST_MARKER[t.list.marker] || 'disc'}}`,
    `${B} ul li{text-align:left;text-indent:0}`,
    `${B} ul li::marker{color:${accent}}`,
  );

  // The first paragraph of a chapter, and the one after a scene break.
  const first = `${B} > [data-heading-level="1"] + p`;
  if (['smallcaps', 'both'].includes(c.first)) {
    css.push(`${first}::first-line{text-transform:uppercase;font-size:.8em;letter-spacing:.03em}`);
  }
  if (t.brk.after === 'smallcaps') {
    css.push(`${B} > .${SCENE_BREAK_CLASS} + p::first-line{text-transform:uppercase;font-size:.8em;letter-spacing:.03em}`);
  }
  if (artOn(t)) {
    css.push(...artCss(t, S, { sides, top: t.page.top * 96, bottom: t.page.bottom * 96, inside, outside }));
  }
  if (['dropcap', 'both'].includes(c.first)) {
    const lines = c.drop;
    const size = ((lines - 1) * lead + bpx * 0.7) / 0.7;
    css.push(
      `${first}::first-letter{float:left;font-family:${fontStackFor(c.tFont)};font-size:${n(size)}px;line-height:${n(size * 0.82)}px;padding:${n((lead - bpx) * 0.5 + bpx * 0.05)}px .08em 0 0;margin:0;color:${accent};font-style:normal;font-weight:${Math.max(400, c.tWeight)};text-transform:none}`,
      `@supports ((initial-letter:2) or (-webkit-initial-letter:2)){${first}::first-letter{float:none;font-size:inherit;line-height:inherit;padding:0;margin-right:.08em;-webkit-initial-letter:${lines};initial-letter:${lines}}}`,
    );
  }
  return css.join('\n');
}

// --------------------------------------------------------------------------
// Old formatting that would sit on top of any theme

// What a theme decides and an inline style overrides. Bold, italic and colour
// are left alone: those are the author marking a passage, not a house style.
const BODY_PROPS = ['font-family', 'font-size', 'line-height'];
const HEADING_PROPS = [...BODY_PROPS, 'font-weight', 'font-style', 'text-align', 'letter-spacing', 'text-transform'];

// A scene break typed as text — what Word import and a lot of typing leave.
const TYPED_BREAK = /^\s*(?:(?:\*\s*){3}|#|⁂|❦|(?:•\s*){3}|~)\s*$/;

function bodiesOf(root) {
  const bodies = root.querySelectorAll('.rv-page-body');
  return bodies.length ? Array.from(bodies) : [root];
}

function overridesIn(root) {
  const styled = [];
  const breaks = [];
  bodiesOf(root).forEach((body) => {
    Array.from(body.children).forEach((block) => {
      if (block.tagName === 'P' && !block.hasAttribute('data-heading-level')
        && TYPED_BREAK.test(block.textContent || '') && !block.querySelector('img')) {
        breaks.push(block);
        return;
      }
      const heading = block.hasAttribute('data-heading-level') || /^H[1-6]$/.test(block.tagName);
      const props = heading ? HEADING_PROPS : BODY_PROPS;
      [block, ...block.querySelectorAll('*')].forEach((el) => {
        if (el.tagName === 'FONT') { styled.push({ el, props, font: true }); return; }
        if (el.closest('.rv-resizable-image, .rv-table-wrap, .rv-textbox')) return;
        if (props.some((p) => el.style?.getPropertyValue(p))) styled.push({ el, props });
      });
    });
  });
  return { styled, breaks };
}

/** How many places in the manuscript carry formatting that hides the theme. */
export function countOverrides(root) {
  if (!root) return 0;
  const { styled, breaks } = overridesIn(root);
  return styled.length + breaks.length;
}

/**
 * Take out the formatting that hides the theme: inline fonts, sizes and line
 * spacing everywhere, plus weight, case and alignment on headings; and turn a
 * scene break typed as "* * *" into a real one. Bold, italic and colour stay.
 * Changes `root` in place and returns how many places it changed.
 */
export function clearOverrides(root) {
  if (!root) return 0;
  const { styled, breaks } = overridesIn(root);
  const doc = root.ownerDocument;
  styled.forEach(({ el, props, font }) => {
    if (font) {
      el.replaceWith(...Array.from(el.childNodes));
      return;
    }
    props.forEach((p) => el.style.removeProperty(p));
    if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
    // A span that only carried the formatting is nothing now.
    if (el.tagName === 'SPAN' && el.attributes.length === 0) el.replaceWith(...Array.from(el.childNodes));
  });
  breaks.forEach((p) => {
    const br = doc.createElement('div');
    br.className = SCENE_BREAK_CLASS;
    br.setAttribute('contenteditable', 'false');
    p.replaceWith(br);
  });
  return styled.length + breaks.length;
}

/** A new scene-break block, as the editor inserts it. */
export function makeSceneBreak(doc = document) {
  const el = doc.createElement('div');
  el.className = SCENE_BREAK_CLASS;
  el.setAttribute('contenteditable', 'false');
  return el;
}
